
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Check, X, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

type Company = {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  plan: string;
};

export const PlanManagement = ({
  company,
  onCompanyUpdate,
}: {
  company: Company;
  onCompanyUpdate: () => Promise<void>;
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  const handleUpgrade = async (planId: string) => {
    if (hasActiveSubscription) {
      toast.error("You already have an active subscription");
      await onCompanyUpdate();
      return;
    }

    setIsLoading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const { data, error } = await supabase.functions.invoke(
        "create-checkout",
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          body: {
            priceId: planId,
          },
        }
      );

      if (error) {
        console.error("Edge Function Error:", error);
        toast.error("Failed to start checkout process. Please try again.");
        return;
      }

      if (data.error) {
        toast.error(data.error);
        await onCompanyUpdate();
        return;
      }

      if (data.url) {
        // Use window.location.href to redirect to the Stripe checkout page
        window.location.href = data.url;
      } else {
        toast.error("No checkout URL received. Please try again.");
      }
    } catch (error: any) {
      console.error("Error:", error);
      toast.error("Failed to start checkout process. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDowngrade = async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const { error } = await supabase.functions.invoke("cancel-subscription", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error("Error:", error);
        toast.error("Failed to cancel subscription. Please try again.");
        return;
      }

      toast.success(
        "Successfully downgraded to Free plan! Please refresh the page."
      );
      await onCompanyUpdate();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to cancel subscription. Please try again.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-3 gap-6">
        {/* Free Plan Card */}
        <Card className="p-6 relative">
          {company.plan === "free" && (
            <Badge className="absolute top-4 right-4">Current Plan</Badge>
          )}
          <div className="space-y-2">
            <h3 className="text-2xl font-bold">Free Plan</h3>
            <p className="text-muted-foreground">Perfect for getting started</p>
            <div className="text-3xl font-bold mt-4">$0/month</div>
          </div>

          <div className="space-y-4 mt-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Up to 10 feedback items</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>1 Feedback Board</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>1 Roadmap</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Custom Feedback Page</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>1 team member</span>
              </div>
              <div className="flex items-center gap-2">
                <X className="w-5 h-5 text-red-500" />
                <span className="text-muted-foreground">Our Branding</span>
              </div>
              <div className="flex items-center gap-2">
                <X className="w-5 h-5 text-red-500" />
                <span className="text-muted-foreground">Custom domains</span>
              </div>
              <div className="flex items-center gap-2">
                <X className="w-5 h-5 text-red-500" />
                <span className="text-muted-foreground">Additional team members</span>
              </div>
            </div>
          </div>

          {company.plan !== "free" ? (
            <Button
              onClick={handleDowngrade}
              className="w-full mt-6"
              variant="outline"
            >
              Downgrade to Free
            </Button>
          ) : (
            <Button className="w-full mt-6" variant="outline" disabled>
              Current Plan
            </Button>
          )}
        </Card>

        {/* Starter Plan Card */}
        <Card className="p-6 relative">
          {company.plan === "starter" && (
            <Badge className="absolute top-4 right-4">Current Plan</Badge>
          )}
          <div className="space-y-2">
            <h3 className="text-2xl font-bold">Starter Plan</h3>
            <p className="text-muted-foreground">For growing businesses</p>
            <div className="text-3xl font-bold mt-4">$49/month</div>
          </div>

          <div className="space-y-4 mt-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>AI Live Chat Integration</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Unlimited feedback items</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Up to 3 feedback boards</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Up to 3 roadmaps</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Custom Feedback Page</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Your Branding</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Custom domains</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Up to 3 team members</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Additional members: $10/month each</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Changelog email notifications</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Unlimited changelogs</span>
              </div>
            </div>
          </div>

          {isCheckingSubscription ? (
            <Button disabled className="w-full mt-6">
              Checking subscription status...
            </Button>
          ) : company.plan === "starter" ? (
            <Button className="w-full mt-6" variant="outline" disabled>
              Already Subscribed
            </Button>
          ) : (
            <Button
              onClick={() => handleUpgrade("price_1RQuRWGporb2rCpUrOwiRLck")}
              disabled={isLoading || hasActiveSubscription}
              className="w-full mt-6"
            >
              {isLoading ? "Processing..." : "Upgrade to Starter"}
            </Button>
          )}
        </Card>

        {/* Enterprise Plan Card */}
        <Card className="p-6 relative border-2 border-primary/50">
          {company.plan === "enterprise" && (
            <Badge className="absolute top-4 right-4">Current Plan</Badge>
          )}
          <div className="absolute -top-4 right-4">
            <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
              Best Value
            </span>
          </div>
          <div className="space-y-2">
            <h3 className="text-2xl font-bold">Enterprise Plan</h3>
            <p className="text-muted-foreground">For larger organizations</p>
            <div className="text-3xl font-bold mt-4">$79/month</div>
          </div>

          <div className="space-y-4 mt-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>AI Live Chat Integration</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Unlimited feedback items</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Unlimited feedback boards</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Unlimited roadmaps</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Custom Feedback Page</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Your Branding</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Custom domains</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Up to 5 team members</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Additional members: $10/month each</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Priority support</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Changelog email notifications</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Unlimited changelogs</span>
              </div>
            </div>
          </div>

          {isCheckingSubscription ? (
            <Button disabled className="w-full mt-6">
              Checking subscription status...
            </Button>
          ) : company.plan === "enterprise" ? (
            <Button className="w-full mt-6" variant="outline" disabled>
              Already Subscribed
            </Button>
          ) : (
            <Button
              onClick={() => handleUpgrade("price_1RQuSCGporb2rCpUkOu5kfop")}
              disabled={isLoading || hasActiveSubscription}
              className="w-full mt-6"
            >
              {isLoading ? "Processing..." : "Upgrade to Enterprise"}
            </Button>
          )}
        </Card>
      </div>
    </div>
  );
};
